import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager, ContextObjectConfig, InputContextMapping, InputFieldMapping } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { CellFilterService } from 'src/app/common/bhub-common-module/services/cell-filter.service';
import { DatasetService } from 'src/app/services/dataset.service';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { Dataset, DataTableSchema } from 'src/app/services/models/datasets.models';
import { SortCriteria } from 'src/app/services/models/dynamic-table.models';
import { Subject, takeUntil, forkJoin, debounceTime, switchMap, EMPTY, of } from 'rxjs';
import { LookupDialogComponent, LookupDialogData, LookupDialogResult } from './lookup-dialog/lookup-dialog.component';

interface SelectOption {
  value: any;
  label: string;
}

@Component({
  selector: 'bhub-input-field-cell',
  standalone: false,
  templateUrl: './input-field-cell.component.html',
  styleUrl: './input-field-cell.component.scss'
})
export class InputFieldCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();

  // UI Properties
  labelText: string = '';
  inputValue: any = '';
  inputType: 'text' | 'dropdown' | 'lookup' = 'text';
  dropdownOptions: SelectOption[] = [];
  placeholder: string = '';

  // Lookup properties
  lookupDisplayValue: string = '';
  lookupStoredValue: any = null;

  // Configuration properties
  dataSource: 'context' | 'dataset' | 'none' = 'none';
  contextObjectKey: string = '';
  fieldName: string = '';
  datasetId: number | null = null;
  referenceDatasetId: number | null = null;

  // State
  isLoadingOptions = false;
  private destroy$ = new Subject<void>();

  // For dataset-driven dropdowns: remember the full source row keyed by the option's value
  // so additional field mappings can pull other fields from the selected row.
  private dropdownRowsByValue: Map<any, any> = new Map();

  // Debounced dataset loading to prevent duplicate requests
  private loadDatasetOptions$ = new Subject<{datasetId: number, displayField: string, valueField: string}>();
  private currentOptionsKey: string = '';

  // Debounced additional context reload on input change
  private inputChangeContextReload$ = new Subject<void>();

  constructor(
    private datasetService: DatasetService,
    private dynamicTableService: DynamicTableService,
    private cellFilterService: CellFilterService,
    private dialog: MatDialog
  ) {
    super();

    // Set up debounced dataset options loading to prevent duplicate requests
    this.loadDatasetOptions$
      .pipe(
        debounceTime(100), // Wait 100ms between requests
        switchMap(({ datasetId, displayField, valueField }) => {
          const optionsKey = `${datasetId}-${displayField}-${valueField}`;
          if (optionsKey === this.currentOptionsKey && this.dropdownOptions.length > 0) {
            return EMPTY;
          }
          this.currentOptionsKey = optionsKey;
          this.isLoadingOptions = true;

          // Load dataset schema first to resolve field IDs for minimal fetch mode
          return this.datasetService.getDatasetReferences().pipe(
            switchMap((datasetsMap: Map<number, Dataset>) => {
              const filters = this.cellFilterService.buildCellFilters(this.cell?.dataBinding?.cellFilters, this.viewManager);
              const orderByConfig = this.getConfigValue('orderBy', []) as any[];
              const orderBy: SortCriteria[] = orderByConfig.map((s: any) => ({ fieldId: s.fieldId, direction: s.direction }));

              // Determine selectFields based on fetch mode
              let selectFields: number[] | undefined;
              const fetchMode = this.getConfigValue('optionsFetchMode', 'all') as string;
              if (fetchMode === 'minimal') {
                const dataset = datasetsMap.get(datasetId);
                if (dataset) {
                  const fieldIds = new Set<number>();
                  for (const schema of dataset.dataTableSchemas) {
                    if (schema.fieldName === displayField || schema.fieldName === valueField) {
                      fieldIds.add(schema.fieldId);
                    }
                  }
                  if (fieldIds.size > 0) {
                    selectFields = Array.from(fieldIds);
                  }
                }
              }

              return forkJoin({
                datasets: of(datasetsMap),
                data: this.dynamicTableService.selectEntries(datasetId, -1, undefined, filters, this.getConfigValue('distinct', false) as boolean, orderBy, selectFields)
              });
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          const datasetId = this.extractDatasetIdFromKey(this.currentOptionsKey);
          const { displayField, valueField } = this.extractFieldsFromKey(this.currentOptionsKey);

          const dataset = result.datasets.get(datasetId);
          if (dataset && result.data.rowData) {
            // Convert actual data to dropdown options
            this.dropdownOptions = this.convertDataToOptions(result.data.rowData, displayField, valueField);
            // Cache full source rows keyed by value so additional field mappings can read sibling fields
            this.dropdownRowsByValue = new Map();
            for (const row of result.data.rowData) {
              const key = row?.[valueField];
              if (key != null) this.dropdownRowsByValue.set(key, row);
            }
          } else {
            this.dropdownOptions = [];
            this.dropdownRowsByValue = new Map();
          }
          this.isLoadingOptions = false;
        },
        error: (error) => {
          console.error('[InputField] Error loading options:', error);
          this.dropdownOptions = [];
          this.isLoadingOptions = false;
          this.inputType = 'text';
          this.currentOptionsKey = '';
        }
      });

    // Set up debounced reload for additional contexts on input change
    this.inputChangeContextReload$
      .pipe(
        debounceTime(1750),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadAdditionalContexts('onChange');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private extractDatasetIdFromKey(key: string): number {
    const parts = key.split('-');
    return parseInt(parts[0], 10);
  }

  private extractFieldsFromKey(key: string): { displayField: string, valueField: string } {
    const parts = key.split('-');
    return {
      displayField: parts[1],
      valueField: parts[2]
    };
  }

  protected initializeCell(): void {
    this.updateFieldConfiguration();
    this.initializeContext(); // Ensure context exists
    this.loadDropdownOptions(); // Load options first to determine input type
    this.loadInitialValue();
    this.loadAdditionalContexts('init'); // Load additional contexts configured for init
    this.loadAdditionalContexts('onChange'); // Also load onChange contexts with whatever value is present
  }

  private initializeContext(): void {
    if (!this.viewManager) return;

    // Initialize context data if needed
    if (!this.viewManager.contextData) {
      this.viewManager.contextData = {};
    }

    // Initialize context for Input Field cells that manage their own context
    if (this.dataSource === 'context' && this.contextObjectKey) {
      // Initialize the context key if it doesn't exist
      if (!this.viewManager.contextData.hasOwnProperty(this.contextObjectKey)) {
        // Initialize with empty object that can hold field values
        this.viewManager.contextData[this.contextObjectKey] = {};
      }
    }

    // Initialize context variable for independent fields
    if (this.dataSource === 'none') {
      const contextVariable = this.getConfigValue('contextVariable', '');
      if (contextVariable) {
        // Store as object so other cells can reference contextVariable.value
        this.viewManager.contextData[contextVariable] = { value: this.inputValue || '' };
      }
    }
  }

  protected onCellOrDataChange(): void {
    this.updateFieldConfiguration();
    this.initializeContext(); // Ensure context exists
    this.loadDropdownOptions(); // Load options first to determine input type
    this.loadInitialValue();
  }

  protected onCellUpdate(): void {
    this.updateFieldConfiguration();
    this.initializeContext(); // Ensure context exists
    this.loadDropdownOptions(); // Load options first to determine input type
    this.loadInitialValue();
  }

  protected onDataSet(data: any): void {
    // Update value when external data changes
    this.loadInitialValue();
  }

  reloadData(): void {
    // Override to refresh field configuration and value when context data changes
    this.updateFieldConfiguration();
    this.loadInitialValue();
    this.loadDropdownOptions();
  }

  private updateFieldConfiguration(): void {
    if (!this.cell) return;

    // Get basic configuration
    this.labelText = this.getConfigValue('label', '');
    this.placeholder = this.getConfigValue('placeholder', '');

    // Determine data source
    this.dataSource = this.getConfigValue('dataSource', 'none') as 'context' | 'dataset' | 'none';

    // Get data binding configuration
    this.contextObjectKey = this.getDataBindingValue('dataObjectKey') || '';
    this.fieldName = this.getDataBindingValue('fieldName') || '';
    this.datasetId = this.getDataBindingValue('datasetId') || null;
  }

  private loadInitialValue(): void {
    let storedValue: any = null;

    if (this.dataSource === 'context' && this.contextObjectKey && this.fieldName && this.viewManager) {
      const dataObject = this.viewManager.contextData[this.contextObjectKey];
      if (dataObject) {
        if (Array.isArray(dataObject) && dataObject.length > 0) {
          storedValue = dataObject[0][this.fieldName] !== undefined ? dataObject[0][this.fieldName] : '';
        } else if (typeof dataObject === 'object' && dataObject !== null) {
          storedValue = dataObject[this.fieldName] !== undefined ? dataObject[this.fieldName] : '';
        }
      }
      this.inputValue = storedValue;
    } else if (this.dataSource === 'dataset' && this.datasetId && this.fieldName) {
      // For dataset source, we might want to load data from the dataset
      // This would typically be handled by loading data from the service
      this.inputValue = '';
    } else {
      // Independent field - value is managed locally
      const defaultValue = this.getConfigValue('defaultValue', '');
      this.inputValue = defaultValue !== undefined ? defaultValue : '';
      storedValue = this.inputValue;
    }

    // For lookup input type, resolve the display value
    const inputType = this.getConfigValue('inputType', 'free') as string;
    if (inputType === 'lookup' && storedValue !== null && storedValue !== undefined && storedValue !== '') {
      this.lookupStoredValue = storedValue;
      this.loadLookupDisplayValue(storedValue);
    } else if (inputType === 'lookup') {
      // No stored value - clear lookup display
      this.lookupDisplayValue = '';
      this.lookupStoredValue = null;
    }
  }

  private loadLookupDisplayValue(storedValue: any): void {
    const lookupDatasetId = this.getConfigValue('lookupDatasetId', null);
    const lookupValueField = this.getConfigValue('lookupValueField', '');
    const lookupDisplayField = this.getConfigValue('lookupDisplayField', '');

    if (!lookupDatasetId || !lookupValueField || !lookupDisplayField) {
      // Can't resolve display value without configuration
      this.lookupDisplayValue = String(storedValue);
      return;
    }

    // Query the lookup dataset to find the row with the matching value field
    this.datasetService.getDatasetReferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (datasetsMap: Map<number, Dataset>) => {
          const dataset = datasetsMap.get(lookupDatasetId);
          if (dataset) {
            // Find the field ID for the value field
            const valueFieldSchema = dataset.dataTableSchemas.find(s => s.fieldName === lookupValueField);
            if (valueFieldSchema) {
              // Query with filter to find the matching row
              const filters = {
                [valueFieldSchema.fieldId]: {
                  values: [storedValue],
                  matchMode: 'exact' as const
                }
              };

              this.dynamicTableService.selectEntries(lookupDatasetId, 1, undefined, filters)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: (response) => {
                    if (response.rowData && response.rowData.length > 0) {
                      const matchedRow = response.rowData[0];
                      this.lookupDisplayValue = matchedRow[lookupDisplayField] !== null && matchedRow[lookupDisplayField] !== undefined
                        ? String(matchedRow[lookupDisplayField])
                        : String(storedValue);

                      // Also restore the context if configured
                      this.restoreLookupContext(matchedRow);
                    } else {
                      // No matching row found - show stored value
                      this.lookupDisplayValue = String(storedValue);
                    }
                  },
                  error: (error) => {
                    console.error('Error loading lookup display value:', error);
                    this.lookupDisplayValue = String(storedValue);
                  }
                });
            } else {
              this.lookupDisplayValue = String(storedValue);
            }
          } else {
            this.lookupDisplayValue = String(storedValue);
          }
        },
        error: (error) => {
          console.error('Error loading dataset for lookup:', error);
          this.lookupDisplayValue = String(storedValue);
        }
      });
  }

  private restoreLookupContext(matchedRow: any): void {
    // If context storage is configured, restore the full row to context
    const lookupCreateContext = this.getConfigValue('lookupCreateContext', false);
    const lookupContextVariable = this.getConfigValue('lookupContextVariable', '');

    if (lookupCreateContext && lookupContextVariable && this.viewManager) {
      if (!this.viewManager.contextData) {
        this.viewManager.contextData = {};
      }

      // Only restore if context doesn't already have data
      if (!this.viewManager.contextData[lookupContextVariable]) {
        this.viewManager.contextData[lookupContextVariable] = {
          ...matchedRow,
          __metadata: {
            datasetId: this.getConfigValue('lookupDatasetId', null),
            restoredAt: new Date().toISOString(),
            isLookupSelection: true
          }
        };
      }
    }
  }


  private loadDropdownOptions(): void {
    // Check the explicit input type configuration (applies regardless of data source)
    const inputType = this.getConfigValue('inputType', 'free') as 'free' | 'static' | 'dataset' | 'lookup';

    if (inputType === 'lookup') {
      // Lookup type - display input with ellipsis button
      this.inputType = 'lookup';
      this.dropdownOptions = [];
      this.isLoadingOptions = false;
      return;
    }

    if (inputType === 'static') {
      // Load static options
      const manualOptions = this.getConfigValue('options', null);
      if (manualOptions && Array.isArray(manualOptions) && manualOptions.length > 0) {
        this.inputType = 'dropdown';
        this.dropdownOptions = manualOptions.map(opt => ({
          value: opt.value !== undefined ? opt.value : opt,
          label: opt.label || String(opt.value !== undefined ? opt.value : opt)
        }));
      } else {
        this.inputType = 'text';
        this.dropdownOptions = [];
      }
      this.isLoadingOptions = false;
      return;
    } else if (inputType === 'dataset') {
      // Load dataset-based options using debounced approach
      const optionsDatasetId = this.getConfigValue('optionsDatasetId', null);
      const optionsDisplayField = this.getConfigValue('optionsDisplayField', '');
      const optionsValueField = this.getConfigValue('optionsValueField', '');


      if (optionsDatasetId && optionsDisplayField && optionsValueField) {
        this.inputType = 'dropdown';
        // Emit to the debounced Subject instead of calling directly
        this.loadDatasetOptions$.next({
          datasetId: optionsDatasetId,
          displayField: optionsDisplayField,
          valueField: optionsValueField
        });
      } else {
        this.inputType = 'text';
        this.dropdownOptions = [];
        this.isLoadingOptions = false;
      }
      return;
    } else if (inputType === 'free') {
      // Free text input
      this.inputType = 'text';
      this.dropdownOptions = [];
      this.isLoadingOptions = false;
      return;
    }


    // Fallback: For legacy support, check for reference fields based on data source
    // This should rarely be used with the new explicit input type configuration
    if (this.dataSource === 'dataset' && this.datasetId && this.fieldName) {
      this.checkForReferenceDataset();
    } else if (this.dataSource === 'context' && this.contextObjectKey && this.fieldName && this.viewManager) {
      // Check context metadata for reference field information
      this.checkContextForReferenceField();
    } else {
      // Default to text input if no options are configured
      this.inputType = 'text';
      this.dropdownOptions = [];
      this.isLoadingOptions = false;
    }
  }

  private checkForReferenceDataset(): void {
    if (!this.datasetId || !this.fieldName) return;

    this.isLoadingOptions = true;
    this.datasetService.getDatasetReferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (datasetsMap: Map<number, Dataset>) => {
          const dataset = datasetsMap.get(this.datasetId!);
          if (dataset) {
            const schema = dataset.dataTableSchemas.find(s => s.fieldName === this.fieldName);
            if (schema && schema.referenceDataId) {
              // This field references another dataset - load it as dropdown options
              this.referenceDatasetId = schema.referenceDataId;
              this.loadReferenceDatasetOptions(schema.referenceDataId);
            } else {
              // No reference dataset - use text input
              this.inputType = 'text';
              this.isLoadingOptions = false;
            }
          } else {
            this.inputType = 'text';
            this.isLoadingOptions = false;
          }
        },
        error: (error) => {
          console.error('Error loading dataset schema:', error);
          this.inputType = 'text';
          this.isLoadingOptions = false;
        }
      });
  }

  private loadReferenceDatasetOptions(referenceDatasetId: number): void {
    // Load the reference dataset to get options
    this.datasetService.getDatasetReferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (datasetsMap: Map<number, Dataset>) => {
          const referenceDataset = datasetsMap.get(referenceDatasetId);
          if (referenceDataset) {
            // Find the primary key or display field
            const primaryKeyField = referenceDataset.dataTableSchemas.find(s => s.isPrimaryKey);
            const displayField = referenceDataset.dataTableSchemas.find(s => s.displayName && !s.isHidden) || primaryKeyField;

            if (displayField) {
              // In a real implementation, we would load actual data from the dataset
              // For now, we'll create mock options
              this.inputType = 'dropdown';
              this.dropdownOptions = this.generateMockReferenceOptions(referenceDataset, displayField);
            } else {
              this.inputType = 'text';
            }
          } else {
            this.inputType = 'text';
          }
          this.isLoadingOptions = false;
        },
        error: (error) => {
          console.error('Error loading reference dataset:', error);
          this.inputType = 'text';
          this.isLoadingOptions = false;
        }
      });
  }

  private generateMockReferenceOptions(dataset: Dataset, displayField: DataTableSchema): SelectOption[] {
    // NO MOCK DATA IN VIEW MODE - This should never be called
    console.error('generateMockReferenceOptions called - this should not happen in view mode');
    return [];
  }

  onInputChange(): void {
    // Update the view manager context if configured
    if (this.dataSource === 'context' && this.contextObjectKey && this.fieldName && this.viewManager) {
      const dataObject = this.viewManager.contextData[this.contextObjectKey];

      if (dataObject) {
        if (Array.isArray(dataObject) && dataObject.length > 0) {
          dataObject[0][this.fieldName] = this.inputValue;
        } else if (typeof dataObject === 'object' && dataObject !== null) {
          dataObject[this.fieldName] = this.inputValue;
        }
        // Emit context change so dependent cells (e.g., calculation cells) can recalculate
        this.contextDataChange.emit({ [this.contextObjectKey]: dataObject });
      }
    } else if (this.dataSource === 'dataset') {
      // For dataset source, no emit needed - data will be saved by Action Button
    } else {
      // For independent fields, update context variable if configured
      const contextVariable = this.getConfigValue('contextVariable', '');
      if (contextVariable && this.viewManager) {
        if (!this.viewManager.contextData) {
          this.viewManager.contextData = {};
        }
        // Store as object so other cells can reference contextVariable.value
        this.viewManager.contextData[contextVariable] = { value: this.inputValue };
        // Emit so dependent cells (sections, calculations) can react
        this.contextDataChange.emit({ [contextVariable]: this.viewManager.contextData[contextVariable] });
      }
    }

    // Apply any additional context mappings configured for this cell
    this.applyInputContextMappings();

    // Trigger debounced reload for free-text onChange additional contexts
    if (this.inputType === 'text') {
      this.inputChangeContextReload$.next();
    }
  }

  onDropdownChange(): void {
    this.onInputChange();
    // Immediate reload for dropdown selections (no debounce needed)
    this.loadAdditionalContexts('onChange');
  }

  /**
   * Applies the cell's configured InputContextMappings — writes the cell's current
   * value (or sibling fields from the selected dropdown row) into one or more
   * target context objects. Mirrors the LookupContextMapping pattern.
   */
  private applyInputContextMappings(): void {
    const mappings = this.getConfigValue<InputContextMapping[]>('inputContextMappings', []) || [];
    if (mappings.length === 0 || !this.viewManager) return;

    if (!this.viewManager.contextData) {
      this.viewManager.contextData = {};
    }

    // For dataset-source dropdowns, look up the full row by the current input value.
    // For other modes, there's no row — only 'value' source is usable.
    const selectedRow = this.dropdownRowsByValue.get(this.inputValue);

    const batchedChanges: { [contextKey: string]: any } = {};

    for (const mapping of mappings) {
      if (!mapping.contextVariable) continue;

      let contextObject = this.viewManager.contextData[mapping.contextVariable];
      if (!contextObject || typeof contextObject !== 'object' || Array.isArray(contextObject)) {
        contextObject = {};
      }

      for (const fm of (mapping.fieldMappings || [])) {
        if (!fm.targetField) continue;
        const value = this.resolveFieldMappingValue(fm, selectedRow);
        if (value !== undefined) {
          contextObject[fm.targetField] = value;
        }
      }

      this.viewManager.contextData[mapping.contextVariable] = contextObject;
      batchedChanges[mapping.contextVariable] = contextObject;
    }

    if (Object.keys(batchedChanges).length > 0) {
      this.contextDataChange.emit(batchedChanges);
    }
  }

  private resolveFieldMappingValue(fm: InputFieldMapping, selectedRow: any): any {
    if (fm.source === 'value') {
      return this.inputValue;
    }
    // 'datasetField' — pull from the selected dropdown row
    if (!selectedRow) return undefined;
    if (fm.sourceFields && fm.sourceFields.length > 0) {
      const separator = fm.separator ?? ' ';
      const parts = fm.sourceFields
        .map(f => selectedRow[f])
        .filter(v => v != null && v !== '');
      return parts.join(separator);
    }
    if (fm.sourceField) {
      return selectedRow[fm.sourceField];
    }
    return undefined;
  }

  /**
   * Loads additional context objects. Filters by loadMode:
   * 'init' = load contexts with loadMode 'init' or undefined (default)
   * 'onChange' = load only contexts with loadMode 'onChange'
   */
  private loadAdditionalContexts(trigger: 'init' | 'onChange'): void {
    const additionalContexts = this.getConfigValue('additionalContexts', []) as ContextObjectConfig[];
    if (!additionalContexts || additionalContexts.length === 0 || !this.viewManager) return;

    for (const config of additionalContexts) {
      const loadMode = config.loadMode || 'init';
      if (loadMode !== trigger) continue;
      if (!config.contextVariable || !config.datasetId) continue;

      // Build filters using cell filter service. A Single Record context fetches with
      // limit 1, so it keeps first-element semantics instead of matching several rows.
      const cellFilters = this.cellFilterService.buildCellFilters(
        config.cellFilters,
        this.viewManager,
        null,
        { multiValue: !config.loadFirstMatch }
      );
      const limit = config.loadFirstMatch ? 1 : 500;

      this.dynamicTableService.selectEntries(config.datasetId, limit, undefined, cellFilters || undefined)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.rowData && response.rowData.length > 0) {
              const contextValue = config.loadFirstMatch
                ? response.rowData[0]
                : response.rowData;
              this.viewManager!.contextData[config.contextVariable] = contextValue;
              this.contextDataChange.emit({ [config.contextVariable]: contextValue });
            } else {
              const emptyCtx = { __metadata: { datasetId: config.datasetId, isEmptyContext: true, isNewRecord: false } };
              this.viewManager!.contextData[config.contextVariable] = emptyCtx;
              this.contextDataChange.emit({ [config.contextVariable]: emptyCtx });
            }
          },
          error: (error) => {
            console.error(`Error loading additional context ${config.contextVariable}:`, error);
          }
        });
    }
  }

  private checkContextForReferenceField(): void {
    const contextData = this.viewManager?.contextData[this.contextObjectKey];

    // Check if context has metadata and field reference information
    if (contextData && contextData.__metadata && contextData.__metadata.fieldReferences) {
      const fieldRef = contextData.__metadata.fieldReferences[this.fieldName];

      if (fieldRef && fieldRef.referenceDataId) {
        // This field has a reference dataset - set up as dropdown
        this.inputType = 'dropdown';
        this.isLoadingOptions = true;
        this.loadReferenceDataOptions(fieldRef.referenceDataId);
        return;
      }
    }

    // No reference field found - default to text input
    this.inputType = 'text';
    this.dropdownOptions = [];
    this.isLoadingOptions = false;
  }

  private loadReferenceDataOptions(referenceDataId: number): void {
    // Load reference dataset options from the dataset service
    this.datasetService.getDatasetReferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (datasetsMap: Map<number, Dataset>) => {
          const referenceDataset = datasetsMap.get(referenceDataId);
          if (referenceDataset) {
            // Generate mock options based on reference dataset (similar to existing mock logic)
            this.dropdownOptions = this.generateMockOptionsForDataset(referenceDataset.datasetReferenceTable.datasetName);
          } else {
            this.dropdownOptions = [];
          }
          this.isLoadingOptions = false;
        },
        error: (error) => {
          console.error('Error loading reference dataset:', error);
          this.dropdownOptions = [];
          this.isLoadingOptions = false;
          // Fallback to text input
          this.inputType = 'text';
        }
      });
  }


  private convertDataToOptions(rowData: any[], displayField: string, valueField: string): SelectOption[] {
    if (!rowData || rowData.length === 0) {
      return [];
    }

    return rowData.map(row => ({
      value: row[valueField],
      label: String(row[displayField] || row[valueField] || 'Unknown')
    })).filter(option => option.value != null); // Filter out null/undefined values
  }

  private generateMockDatasetFieldOptions(dataset: Dataset, displayField: string, valueField: string): SelectOption[] {
    // NO MOCK DATA IN VIEW MODE - This should never be called
    console.error('generateMockDatasetFieldOptions called - this should not happen in view mode');
    return [];
  }

  private generateMockOptionsForDataset(datasetName: string): SelectOption[] {
    // NO MOCK DATA IN VIEW MODE - This should never be called
    console.error('generateMockOptionsForDataset called - this should not happen in view mode');
    return [];
  }

  // Lookup Dialog Methods
  openLookupDialog(): void {
    const lookupDatasetId = this.getConfigValue('lookupDatasetId', null);
    if (!lookupDatasetId) {
      console.error('Lookup dataset not configured');
      return;
    }

    const dialogData: LookupDialogData = {
      datasetId: lookupDatasetId,
      displayColumns: this.getConfigValue('lookupDisplayColumns', []),
      filterColumns: this.getConfigValue('lookupFilterColumns', []),
      valueField: this.getConfigValue('lookupValueField', ''),
      displayField: this.getConfigValue('lookupDisplayField', ''),
      cellFilters: this.getConfigValue('lookupCellFilters', []),
      viewManager: this.viewManager || undefined,
      title: this.labelText ? `Select ${this.labelText.replace(':', '')}` : 'Select an Item',
      allowCreate: this.getConfigValue('lookupAllowCreate', false),
      presetFilterFields: this.getConfigValue('lookupPresetFilterFields', []),
      distinct: this.getConfigValue('distinct', false),
      filterDisplayMode: this.getConfigValue('lookupFilterDisplayMode', 'dialog'),
      inlineFilterMatchMode: this.getConfigValue('lookupInlineFilterMatchMode', '')
    };

    const dialogRef = this.dialog.open(LookupDialogComponent, {
      data: dialogData,
      panelClass: 'lookup-dialog-container',
      disableClose: false,
      autoFocus: false
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: LookupDialogResult | undefined) => {
        if (result) {
          this.handleLookupSelection(result);
        }
      });
  }

  private handleLookupSelection(result: LookupDialogResult): void {
    // Update display value (what's shown in the input)
    this.lookupDisplayValue = result.displayValue !== null && result.displayValue !== undefined
      ? String(result.displayValue)
      : '';

    // Update stored value (what's saved as the cell value)
    this.lookupStoredValue = result.storedValue;
    this.inputValue = result.storedValue;

    // Make the selected row available to input-context mappings configured with
    // source='datasetField' (same behavior as the dataset-dropdown path).
    if (result.selectedRow && this.lookupStoredValue != null) {
      this.dropdownRowsByValue.set(this.lookupStoredValue, result.selectedRow);
    }

    // Check if we should store the entire row in context
    const lookupCreateContext = this.getConfigValue('lookupCreateContext', false);
    const lookupContextVariable = this.getConfigValue('lookupContextVariable', '');

    if (lookupCreateContext && lookupContextVariable && this.viewManager) {
      // Store the entire selected row in the context
      if (!this.viewManager.contextData) {
        this.viewManager.contextData = {};
      }

      this.viewManager.contextData[lookupContextVariable] = {
        ...result.selectedRow,
        __metadata: {
          datasetId: this.getConfigValue('lookupDatasetId', null),
          selectedAt: new Date().toISOString(),
          isLookupSelection: true
        }
      };

      // Emit context change for the lookup context
      this.contextDataChange.emit({
        [lookupContextVariable]: this.viewManager.contextData[lookupContextVariable]
      });
    }

    // Emit the value change
    this.onInputChange();
  }

  clearLookupValue(): void {
    this.lookupDisplayValue = '';
    this.lookupStoredValue = null;
    this.inputValue = null;

    // Clear context if configured
    const lookupCreateContext = this.getConfigValue('lookupCreateContext', false);
    const lookupContextVariable = this.getConfigValue('lookupContextVariable', '');

    if (lookupCreateContext && lookupContextVariable && this.viewManager?.contextData) {
      const emptyCtx = { __metadata: { isEmptyContext: true, isNewRecord: false } };
      this.viewManager.contextData[lookupContextVariable] = emptyCtx;

      this.contextDataChange.emit({
        [lookupContextVariable]: emptyCtx
      });
    }

    this.onInputChange();
  }
}
